import React, { useState, useEffect, useContext } from "react";
import {
  Platform,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Image,
} from "react-native";
import roleConfig from "../../../utils/constants/roles";
import { Text, View } from "../../../components/shared/Themed";
import { getRole } from "../../../services/usersService";
import { getS3SignUrl, pushPhotoToS3 } from "../../../services/s3BucketService";
import Spinner from "../../../components/shared/Spinner";
import { getItemFromSessionStorage } from "../../../services/utils/storageService";
import * as ImagePicker from "expo-image-picker";
import { useNavigation } from "@react-navigation/native";
import { FontAwesome } from "@expo/vector-icons";
import FeatherIcon from "react-native-vector-icons/Feather";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useAppTheme } from "../../../hooks/useAppTheme";
import type { ThemeColors } from "../../../utils/constants/colors";

export default function AccountForm() {
  const companyContext = JSON.parse(
    getItemFromSessionStorage("company-context") as string
  );
  const [companyName, setCompanyName] = useState<string>(
    companyContext ? companyContext.company_name : ""
  );
  const [currentRole, setCurrentRole] = useState<string>("");
  const [language, setLanguage] = useState<string>("English");
  const [timezone, setTimezone] = useState<string>("Australia");

  let disabled = companyContext && companyContext.company_name === "Multisteps";
  if (companyContext) {
    disabled = currentRole === roleConfig.roles.owner ? false : true;
  }

  const logoContext = getItemFromSessionStorage("logo-context") as string;
  const [image, setImage] = useState<string>(logoContext);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [companyId, setCompanyId] = useState<string>(
    companyContext ? companyContext.company_id : null
  );
  const [imageBase64, setImageBase64] = useState<string>("");

  const submitHandler = () => {
    // TODO: wire up submit
  };

  const pickImage = async () => {
    let result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.All,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.3,
      base64: true,
      exif: false,
    });

    if (!result.canceled) {
      if (result.assets && result.assets.length > 0) {
        setImageBase64(result.assets[0].base64 as string);
        setImage(result.assets[0].uri);
      }
    }
  };

  const getImage = async () => {
    setLoading(true);
    companyId &&
      getS3SignUrl(
        "multisteps-go-public-image",
        `logo/${companyId}.png`,
        "getObject",
        "image/*"
      ).then((data) => {
        if (data?.signedUrl) {
          setImage(data.signedUrl);
        }
        setLoading(false);
      });
  };

  const uploadImage = async () => {
    companyId &&
      getS3SignUrl(
        "multisteps-go-public-image",
        `logo/${companyId}.png`,
        "putObject",
        "image/*"
      ).then((data) => {
        setLoading(true);
        pushPhotoToS3(data?.signedUrl, "PUT", imageBase64, "image/*").then(
          () => {
            setLoading(false);
            getImage();
          }
        );
      });
  };

  useEffect(() => {
    let isMounted = true;
    if (companyContext && isMounted) {
      setCompanyName(companyContext.company_name);
    }
    getRole(companyContext.company_id).then((mapiData) => {
      setCurrentRole(mapiData?.role);
    });

    (async () => {
      if (Platform.OS !== "web" && isMounted) {
        const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (status !== "granted") {
          alert("Sorry, Camera roll permissions are required to make this work!");
        }
      }
    })();
    companyContext && setCompanyId(companyContext.company_id);
    logoContext && setImage(logoContext);

    return () => {
      isMounted = false;
    };
  }, []);

  const navigation = useNavigation();
  const { isSmallDevice } = useContext(DeviceOrientationContext);
  const { colors, isDark } = useAppTheme();
  const styles = createStyles(colors, isDark);

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={[styles.scrollContent, isSmallDevice && { paddingHorizontal: 14 }]}
      showsVerticalScrollIndicator={false}
    >
      {/* Page Header */}
      <View style={[styles.pageHeader, isSmallDevice && { paddingHorizontal: 14, marginHorizontal: -14 }]}>
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          {isSmallDevice && (
            <TouchableOpacity
              onPress={() => navigation.navigate("SettingsM")}
              style={styles.backButton}
            >
              <FeatherIcon name="arrow-left" size={18} color="#2563EB" />
            </TouchableOpacity>
          )}
          <View style={{ flex: 1, paddingRight: isSmallDevice ? 10 : 0 }}>
            <Text style={[styles.pageTitle, isSmallDevice && { fontSize: 20 }]} numberOfLines={1}>Organisation Settings</Text>
            <Text style={[styles.pageSubtitle, isSmallDevice && { fontSize: 12 }]} numberOfLines={1}>
              Manage your company details and branding
            </Text>
          </View>
        </View>
      </View>


      {/* ---- Company Logo Card ---- */}
      <View style={styles.card}>
        <Text style={styles.sectionTitle}>Company Logo</Text>
        <View style={styles.sectionDivider} />

        {/* Logo Preview */}
        <View style={styles.logoPreviewBox}>
          <Image
            source={{ uri: image }}
            style={styles.logoImage}
            resizeMode="contain"
          />
        </View>

        {!disabled && (
          <>
            <View style={styles.uploadContainer}>
              <TouchableOpacity style={styles.uploadButton} onPress={pickImage}>
                <Text style={styles.uploadButtonText}>Upload Logo</Text>
              </TouchableOpacity>
              <Text style={styles.uploadHint}>JPG, GIF or PNG. Max size of 5MB</Text>
            </View>

            <View style={styles.sectionDivider} />

            <View style={styles.buttonRow}>
              <TouchableOpacity style={styles.primaryButton} onPress={uploadImage}>
                <Text style={styles.primaryButtonText}>Save Logo</Text>
              </TouchableOpacity>
              {isLoading && (
                <Spinner styles={styles.spinner} size="small" color="#74BBFF" />
              )}
            </View>
          </>
        )}
      </View>

      {/* ---- Company Details Card ---- */}
      <View style={styles.card}>
        <Text style={styles.sectionTitle}>Company Details</Text>
        <View style={styles.sectionDivider} />

        <View style={styles.fieldGroup}>
          <Text style={styles.fieldLabel}>Company Name</Text>
          <TextInput
            style={[styles.input, disabled && styles.inputDisabled]}
            onChangeText={setCompanyName}
            value={companyName}
            editable={!disabled}
            placeholder="Company name"
            placeholderTextColor={colors.textMuted}
          />
        </View>

        <View style={styles.fieldGroup}>
          <Text style={styles.fieldLabel}>Language</Text>
          <TextInput
            style={[styles.input, disabled && styles.inputDisabled]}
            onChangeText={setLanguage}
            value={language}
            editable={!disabled}
            placeholder="Language"
            placeholderTextColor={colors.textMuted}
          />
        </View>

        <View style={styles.fieldGroup}>
          <Text style={styles.fieldLabel}>Country / City</Text>
          <TextInput
            style={[styles.input, disabled && styles.inputDisabled]}
            onChangeText={setTimezone}
            value={timezone}
            editable={!disabled}
            placeholder="Country or city"
            placeholderTextColor={colors.textMuted}
          />
        </View>

        {!disabled && (
          <View style={styles.buttonRow}>
            <TouchableOpacity style={styles.primaryButton} onPress={submitHandler}>
              <Text style={styles.primaryButtonText}>Save Changes</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    </ScrollView>
  );
}

function createStyles(colors: ThemeColors, isDark: boolean) {
  return StyleSheet.create({
    scrollView: {
      flex: 1,
      backgroundColor: colors.background,
    },
    scrollContent: {
      paddingHorizontal: 24,
      paddingBottom: 48,
    },

    /* Page Header */
    pageHeader: {
      paddingTop: 20,
      paddingBottom: 16,
      marginBottom: 16,
      paddingHorizontal: 24,
      marginHorizontal: -24,
      backgroundColor: colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.borderSubtle,
    },
    backButton: {
      width: 38,
      height: 38,
      borderRadius: 12,
      backgroundColor: colors.surface,
      justifyContent: "center",
      alignItems: "center",
      marginRight: 12,
      borderWidth: 1,
      borderColor: colors.borderSubtle,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.06,
      shadowRadius: 3,
      elevation: 1,
    },
    pageTitle: {
      fontSize: 26,
      fontWeight: "800",
      color: colors.text,
      letterSpacing: -0.5,
    },
    pageSubtitle: {
      fontSize: 14,
      color: colors.textSecondary,
      marginTop: 4,
      letterSpacing: 0.1,
    },

    /* Card */
    card: {
      backgroundColor: colors.surface,
      borderRadius: 16,
      paddingVertical: 28,
      paddingHorizontal: 20,
      marginBottom: 20,
      borderWidth: 1,
      borderColor: colors.borderSubtle,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.04,
      shadowRadius: 8,
      elevation: 2,
    },
    sectionTitle: {
      fontSize: 18,
      fontWeight: "700",
      color: colors.text,
      letterSpacing: -0.2,
    },
    sectionDivider: {
      height: 1,
      backgroundColor: colors.borderSubtle,
      marginVertical: 16,
    },

    /* Fields */
    fieldGroup: {
      marginBottom: 16,
    },
    fieldLabel: {
      fontSize: 13,
      fontWeight: "600",
      color: colors.text,
      marginBottom: 6,
      letterSpacing: 0.1,
    },
    input: {
      height: 48,
      backgroundColor: colors.background,
      paddingVertical: 10,
      paddingHorizontal: 14,
      borderColor: colors.borderSubtle,
      borderWidth: 1.5,
      borderRadius: 10,
      fontSize: 14,
      color: colors.text,
    },
    inputDisabled: {
      backgroundColor: colors.surfaceSubtle,
      color: colors.textMuted,
    },

    /* Logo */
    logoPreviewBox: {
      width: "100%",
      maxWidth: 320,
      borderWidth: 1,
      borderColor: colors.borderSubtle,
      borderRadius: 12,
      alignItems: "center",
      justifyContent: "center",
      paddingVertical: 24,
      marginBottom: 20,
      backgroundColor: colors.surfaceSubtle,
      alignSelf: "center",
    },
    logoImage: {
      width: 220,
      height: 120,
    },
    uploadContainer: {
      alignItems: "center",
      marginBottom: 8,
      marginTop: 8,
    },
    uploadButton: {
      paddingVertical: 8,
      paddingHorizontal: 18,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.primary,
      backgroundColor: colors.primaryBg,
      marginBottom: 8,
    },
    uploadButtonText: {
      fontSize: 13,
      fontWeight: "600",
      color: colors.primary,
    },
    uploadHint: {
      fontSize: 12,
      color: colors.textMuted,
    },

    /* Buttons */
    buttonRow: {
      flexDirection: "row",
      alignItems: "center",
      marginTop: 8,
    },
    primaryButton: {
      alignItems: "center",
      justifyContent: "center",
      paddingVertical: 12,
      paddingHorizontal: 28,
      borderRadius: 10,
      backgroundColor: colors.primary,
      shadowColor: colors.primary,
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.2,
      shadowRadius: 8,
      elevation: 3,
    },
    primaryButtonText: {
      fontSize: 14,
      fontWeight: "700",
      color: "#ffffff",
      letterSpacing: 0.3,
    },
    spinner: {
      marginLeft: 12,
    },
  });
}
